import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
// import StarIcon from '@mui/icons-material/Star';
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Tooltip,
  // List,
  // ListItem,
  // ListItemText,
  Typography,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as Yup from 'yup';

import type { BulkUploadLeadsRequest } from '@/types/employment/BulkUploadLeadsRequest';

import Loader from './Loader';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(0),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
  isBulkUploadTemplate?: boolean;
  setIsBulkUploadTemplate?: (value: boolean) => void;
}
export interface BulkTempalte941Props {
  isBulkUploadTemplate: boolean;
  setIsBulkUploadTemplate: React.Dispatch<React.SetStateAction<boolean>>;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 0 }} {...other}>
      {children}
      {onClose ? (
        <Tooltip title="Close" arrow>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              p: 0,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </DialogTitle>
  );
}

const BulkTempalte941 = (props: BulkTempalte941Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const validationSchema = Yup.object().shape({
    UserName: Yup.string().required('Name is required'),
    EmailAddress: Yup.string()
      .required('Email Address is required')
      .email('Enter valid Email Address'),
  });

  const defaultValue: BulkUploadLeadsRequest = {
    EmailAddress: '',
    UserName: '',
    Category: '',
    Token: '',
  };

  const { handleSubmit, control, reset } = useForm<BulkUploadLeadsRequest>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema) as unknown as Resolver<
      BulkUploadLeadsRequest,
      any
    >,
    defaultValues: defaultValue,
  });

  React.useEffect(() => {
    const loadScriptByURL = (id: any, url: any, callback: any) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        // eslint-disable-next-line func-names
        script.onload = function () {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}&badge=bottomleft`,
      // eslint-disable-next-line func-names
      function () {
        // console.log('Script loaded!');
      }
    );
  }, []);

  const onSubmit = async (data: BulkUploadLeadsRequest) => {
    setIsLoading(true);
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY, {
          action: 'submit',
        })
        .then(async (token: any) => {
          /* send data to the server */
          const postData: BulkUploadLeadsRequest = data;
          postData.Category = 'Form941BulkUploadTemplate';
          postData.Token = token;
          const address = `/api/bulkuploadlead/`;
          const contactusResponse = await fetch(address, {
            method: 'POST',
            body: JSON.stringify(postData),
          });
          const contactusResponseData = await contactusResponse.json();
          if (contactusResponseData === true) {
            reset(defaultValue);
            // Trigger file download
            const link = document.createElement('a');
            link.href =
              'https://s3.us-east-1.amazonaws.com/taxzerone.dev/StaticFiles/Employment/Form941/BulkUploadTemplates/TaxZerone_Form_941_Template.xlsx';
            link.download = 'TaxZerone_Form_941_Template.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            props.setIsBulkUploadTemplate(false);
          }
          setIsLoading(false);
        })
        .catch(() => {});
    });
  };

  const handleClose = () => {
    props.setIsBulkUploadTemplate(false);
  };

  useEffect(() => {
    reset(defaultValue);
  }, [props.isBulkUploadTemplate]);

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={props.isBulkUploadTemplate}
      className="holiday-theme"
      fullWidth
      maxWidth="md"
    >
      <Loader isLoading={isLoading} />
      <BootstrapDialogTitle
        id="customized-dialog-title"
        onClose={handleClose}
      />
      <DialogContent>
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          justifyContent="space-between"
          alignItems="stretch"
          spacing={0}
        >
          <Box
            component="form"
            autoComplete="off"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
              width: '100%',
              p: 3,
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 600,
                color: '#000000',
                fontSize: { md: 28, sm: 20, xs: 18 },
                mb: 2,
              }}
            >
              Download the{' '}
              <span style={{ fontWeight: 700, color: '#1da94a' }}>
                Form 941 Bulk Upload
              </span>{' '}
              Template Now!
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontSize: 17, fontWeight: 500, mb: 2 }}
            >
              Get IRS Form 941 template for free—just enter your details!{' '}
            </Typography>
            <Box sx={{ textAlign: 'center' }}>
              <Controller
                name="UserName"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    id="UserName"
                    name="UserName"
                    label="*Name"
                    color="secondary"
                    variant="outlined"
                    className={value ? 'prefilled' : 'mandatory'}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    inputProps={{
                      maxLength: 100,
                      maxHight: 100,
                    }}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
              <Controller
                name="EmailAddress"
                control={control}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TextField
                    className={value ? 'prefilled' : 'mandatory'}
                    id="EmailAddress"
                    label="*Email Address"
                    variant="outlined"
                    autoComplete="nope"
                    color="secondary"
                    inputProps={{
                      maxLength: 100,
                    }}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error ? error.message : null}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
              />
              <Button
                variant="contained"
                size="large"
                type="submit"
                sx={{
                  textTransform: 'none',
                  boxShadow: 'none',
                  borderRadius: 4,
                  display: 'inline-block',
                  fontSize: { xs: 18, sm: 20, md: 22, lg: 24 },
                }}
              >
                Download Now
              </Button>
              <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                  *Your information is secure with us. It will only be used to
                  send important tax filing updates related to Form 941.{' '}
                </Typography>
              </Alert>
            </Box>
          </Box>
          <Box
            sx={{
              maxWidth: 300,
              width: '100%',
              minHeight: { xs: 339, sm: 380, md: 450 },
              minWidth: { xs: 200, sm: 339, md: 370 },
              display: { xs: 'none', sm: 'block' },
            }}
            className="template-popup"
          />
        </Stack>
      </DialogContent>
    </BootstrapDialog>
  );
};

export default BulkTempalte941;
